import { useState } from 'react';
import { contactFormSchema, type ContactFormData } from '@/lib/validations';
import { toast } from 'react-hot-toast';

/**
 * R2 - Custom hook for contact form logic
 * R3 - Includes validation and error handling
 */
export function useContactForm(whatsappNumber = '34669375242') {
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    phone: '',
    message: '',
  });
  const [errors, setErrors] = useState<Partial<Record<keyof ContactFormData, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    
    // Clear error for this field when user starts typing
    if (errors[name as keyof ContactFormData]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    try {
      contactFormSchema.parse(formData);
      setErrors({});
      return true;
    } catch (error) {
      if (error instanceof Error && 'errors' in error) {
        const zodError = error as { errors: Array<{ path: string[]; message: string }> };
        const newErrors: Partial<Record<keyof ContactFormData, string>> = {};
        
        zodError.errors.forEach((err) => {
          const field = err.path[0] as keyof ContactFormData;
          newErrors[field] = err.message;
        });
        
        setErrors(newErrors);
      }
      return false;
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Por favor corrige los errores en el formulario');
      return;
    }

    setIsSubmitting(true);

    // R1 - Sanitize data before sending to WhatsApp
    const whatsappMessage = `Nombre: ${formData.name}\nTeléfono: ${formData.phone}\nMensaje: ${formData.message}`;
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(whatsappMessage)}`;
    
    // Open WhatsApp in new window
    window.open(whatsappUrl, '_blank');
    
    // Show success message and reset form
    toast.success('¡Formulario enviado! Te contactaremos pronto.');
    
    setTimeout(() => {
      setFormData({ name: '', phone: '', message: '' });
      setIsSubmitting(false);
    }, 1500);
  };

  return {
    formData,
    errors,
    isSubmitting,
    handleChange,
    handleSubmit,
  };
}
